import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, UserRole } from '../types';
import { api } from '../constants';

interface AuthContextType {
  user: User | null;
  login: (email: string, role: UserRole, businessName: string, additionalData?: any) => Promise<void>;
  updateUser: (data: Partial<User>) => void;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const savedUser = localStorage.getItem('piggy_dental_user');
    if (savedUser) {
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      
      // Background refresh from API
      api.getUser(parsedUser.id)
        .then(freshUser => {
          console.log('AuthContext: Background refresh successful');
          setUser(freshUser);
          localStorage.setItem('piggy_dental_user', JSON.stringify(freshUser));
        })
        .catch(err => {
          console.warn('AuthContext: Background refresh failed', err);
        });
    }
  }, []);

  const login = async (email: string, role: UserRole, businessName: string, additionalData?: any) => {
    try {
      console.log('Attempting API login for:', email);
      // Try API login first
      const response = await api.login(email, role, businessName, additionalData);
      console.log('API login successful:', response.user.id);
      setUser(response.user);
      localStorage.setItem('piggy_dental_user', JSON.stringify(response.user));
      if (response.token) {
        localStorage.setItem('auth_token', response.token);
      }
    } catch (error: any) {
      console.error('API login error:', error.message);
      
      // If it's a known validation/auth error from our API, don't mock it
      if (error.message && !error.message.includes('Failed to fetch')) {
          throw error; 
      }

      // Fallback to mock login ONLY if API is unreachable (Offline mode)
      const newUser: User = {
        id: 'mock_' + Math.random().toString(36).substr(2, 9),
        email,
        role,
        businessName,
        hasPaymentMethod: false,
        ...additionalData
      };
      setUser(newUser);
      localStorage.setItem('piggy_dental_user', JSON.stringify(newUser));
    }
  };

  const updateUser = (data: Partial<User>) => {
    console.log('AuthContext: Updating user with data:', data);
    setUser(prev => {
      if (!prev) return null;
      const updated = { ...prev, ...data };
      console.log('AuthContext: New User State:', updated);
      localStorage.setItem('piggy_dental_user', JSON.stringify(updated));
      return updated;
    });
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('piggy_dental_user');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('needs_payment_setup');
  };

  const isAuthenticated = !!user;
  const isAdmin = user?.role === 'ADMIN';

  return (
    <AuthContext.Provider value={{ user, login, updateUser, logout, isAuthenticated, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};